import { Repeat, Sparkles, Compass, ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import heritageImg from "@/assets/heritage-mood-2.jpg.asset.json";
import brollopImg from "@/assets/brollop-30.webp.asset.json";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { trackCta } from "@/lib/analytics";

// Tre tydliga köpvägar (Prioritet A — startsidans köpcopy + CTA-hierarki).
// Hjälper besökaren att direkt se vilket spår som matchar deras behov:
// löpande (Pulse), enskild produktion (Prime) eller test först (Pilot).

const paths = [
  {
    id: "pulse",
    icon: Repeat,
    eyebrow: "Löpande flöde",
    title: "Pulse",
    body: "Film på abonnemang. Ett jämnt månadsflöde av rörligt innehåll till fast pris — utan offertspel.",
    points: ["Fast månadspris", "Första klippet inom 10–14 dagar", "Säg upp månadsvis"],
    cta: "Utforska Pulse",
    to: "/pulse",
    featured: true,
  },
  {
    id: "prime",
    icon: Sparkles,
    eyebrow: "Enskild produktion",
    title: "Prime",
    body: "Större kampanjer, brandfilmer och produktioner med dedikerat team — fast pris per projekt.",
    points: ["Offert per projekt", "Senior crew", "Inga dolda påslag"],
    cta: "Utforska Prime",
    to: "/prime",
    featured: false,
  },
  {
    id: "pilot",
    icon: Compass,
    eyebrow: "Testa först",
    title: "Pilot",
    body: "En avgränsad första produktion. Se vårt tempo och vår kvalitet innan ni binder er.",
    points: ["Lågtröskel-start", "Ingen bindning", "Växla upp när ni vill"],
    cta: "Starta ett Pilot",
    to: "/pilot",
    featured: false,
  },
] as const;

export function ChooseYourPath() {
  return (
    <Section className="bg-surface/30">
      <EyebrowHeading
        eyebrow="Välj er väg"
        title={<>Tre sätt att jobba med oss — <span className="text-mint">välj efter behov</span>.</>}
        lede="Behöver ni ett löpande innehållsflöde, en enskild produktion eller vill ni testa oss först? Här är vägarna in."
      />

      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {paths.map((p) => (
          <article
            key={p.id}
            className={`flex flex-col rounded-3xl border p-7 transition-colors ${
              p.featured
                ? "border-mint/40 bg-mint/[0.06]"
                : "border-white/10 bg-surface hover:border-white/25"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-mint/15 text-mint ring-1 ring-mint/30">
                <p.icon className="h-5 w-5" strokeWidth={2} />
              </div>
              <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {p.eyebrow}
              </span>
            </div>
            <h3 className="mt-5 text-2xl font-semibold">{p.title}</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            <ul className="mt-5 space-y-2 text-sm">
              {p.points.map((pt) => (
                <li key={pt} className="flex items-center gap-2 text-foreground/85">
                  <span className="h-1.5 w-1.5 rounded-full bg-mint" />
                  {pt}
                </li>
              ))}
            </ul>
            <Link
              to={p.to}
              onClick={() => trackCta("choose_path", { location: "home_choose_path", label: p.title, path: p.id })}
              className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-mint transition-colors hover:text-foreground"
            >
              {p.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </article>
        ))}
      </div>

      {/* Sekundära spår — Heritage & Bröllopsfilm, mindre framträdande men visuellt närvarande */}
      <div className="mt-16 border-t border-white/10 pt-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Vid sidan om Pulse, Prime &amp; Pilot
          </div>
          <h3 className="mt-2 text-xl font-semibold md:text-2xl">
            Två specialformat — samma hantverk
          </h3>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          {[
            {
              to: "/heritage" as const,
              id: "heritage",
              eyebrow: "Heritage",
              title: "Livsberättelser & kunskapsarv",
              body: "Dokumentär film om människorna, hantverket och historien — för familjer, experter och varumärken som vill bevara arvet.",
              img: heritageImg.url,
              alt: "Heritage — porträtt på film",
            },
            {
              to: "/brollopsfilm" as const,
              id: "brollopsfilm",
              eyebrow: "Bröllopsfilm",
              title: "Filmen från er dag",
              body: "Cinematisk bröllopsfilm med samma filmiska känsla som våra varumärkesproduktioner. 1–3 bröllop per år.",
              img: brollopImg.url,
              alt: "Bröllopsfilm — stillbild",
            },
          ].map((s) => (
            <Link
              key={s.id}
              to={s.to}
              onClick={() => trackCta("choose_path", { location: "home_choose_path_secondary", label: s.eyebrow, path: s.id })}
              className="group relative flex overflow-hidden rounded-2xl border border-white/10 bg-surface transition-colors hover:border-white/25"
            >
              <div className="relative w-2/5 shrink-0 overflow-hidden sm:w-1/3">
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-surface/70" />
              </div>
              <div className="flex flex-1 flex-col justify-center p-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {s.eyebrow}
                </div>
                <h4 className="mt-2 text-lg font-semibold leading-snug md:text-xl">
                  {s.title}
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {s.body}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-mint">
                  Utforska {s.eyebrow}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </Section>
  );
}
